import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import PageTitle from "../../../../layouts/PageTitle";
import {
  appApi,
  useGetContractorByIdQuery,
  useGetContractorCategoriesQuery,
} from "../../../../../store/appApi";
import { updateContractorApi } from "../../../../../services/contractor";
import { selectHasModuleAccess } from "../../../../../store/authSlice";

const EditContractor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hasContractorsWrite = useSelector(selectHasModuleAccess("contractors", "read-write"));

  // ── GET contractor details via Redux ──────────────────────────────────────
  const { data: contractorResponse, isLoading: loadingContractor } =
    useGetContractorByIdQuery(id, { skip: !id });
  const contractorData = contractorResponse?.contractor || null;

  // ── GET categories via Redux ───────────────────────────────────────────────
  const { data: catResponse } = useGetContractorCategoriesQuery();
  const categories = Array.isArray(catResponse?.data) ? catResponse.data : [];

  const [form, setForm] = useState({
    name: "",
    phone: "",
    address: "",
    gstNumber: "",
    categoryName: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // ── Pre-populate form when contractor data arrives ─────────────────────────
  useEffect(() => {
    if (!contractorData) return;
    setForm({
      name: contractorData.name || "",
      phone: contractorData.phone || "",
      address: contractorData.address || "",
      gstNumber: contractorData.gstNumber || "",
      // categoryName is populated — extract the ID string
      categoryName:
        contractorData.categoryName?._id?.toString() ||
        contractorData.categoryName?.id ||
        contractorData.categoryName?.toString() ||
        "",
    });
  }, [contractorData]);

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Contractor name is required.";
    return errs;
  };

  // ── PATCH via service — no Redux ──────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setSubmitting(true);
    try {
      await updateContractorApi(id, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        gstNumber: form.gstNumber.trim(),
        categoryName: form.categoryName || null,
      });
      dispatch(appApi.util.invalidateTags(["Contractors"]));
      Swal.fire({ icon: "success", title: "Contractor updated!", timer: 1500, showConfirmButton: false });
      navigate("/contractor-list");
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to update contractor.";
      Swal.fire({ icon: "error", title: "Error", text: msg });
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasContractorsWrite) return <Navigate to="/contractor-list" replace />;
  if (loadingContractor) {
    return (
      <>
        <PageTitle activeMenu={"Edit Contractor"} motherMenu={"Inventory"} />
        <div className="row"><div className="col-12"><p>Loading...</p></div></div>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu={"Edit Contractor"} motherMenu={"Inventory"} />
      <div className="row">
        <div className="col-xl-12 col-xxl-12 col-sm-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Edit Contractor</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  {/* Category dropdown — data via Redux */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">Contractor Category</label>
                      <select
                        className="form-control"
                        value={form.categoryName}
                        onChange={set("categoryName")}
                      >
                        <option value="">— Select category —</option>
                        {categories.map((cat) => (
                          <option key={cat.id || cat._id} value={cat.id || cat._id}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group">
                      <label htmlFor="name" className="form-label">Contractor Name*</label>
                      <input
                        id="name"
                        type="text"
                        className="form-control"
                        value={form.name}
                        onChange={set("name")}
                        required
                      />
                      {errors.name && <small className="text-danger">{errors.name}</small>}
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group">
                      <label htmlFor="phone" className="form-label">Phone</label>
                      <input
                        id="phone"
                        type="text"
                        className="form-control"
                        value={form.phone}
                        onChange={set("phone")}
                      />
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group">
                      <label htmlFor="gstNumber" className="form-label">GST Number</label>
                      <input
                        id="gstNumber"
                        type="text"
                        className="form-control"
                        value={form.gstNumber}
                        onChange={set("gstNumber")}
                      />
                    </div>
                  </div>

                  <div className="col-lg-12">
                    <div className="form-group">
                      <label htmlFor="address" className="form-label">Address</label>
                      <textarea
                        id="address"
                        className="form-control"
                        rows="3"
                        value={form.address}
                        onChange={set("address")}
                      />
                    </div>
                  </div>

                  <div className="col-lg-12">
                    <button type="submit" className="btn btn-primary me-1" disabled={submitting}>
                      {submitting ? "Saving..." : "Save Changes"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger light"
                      onClick={() => navigate("/contractor-list")}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditContractor;
